import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import ContractPreviewModal from "@/components/application/merchant-tasks/send/ContractPreviewModal";
import { DecisionColumn } from "@/components/underwriting/DecisionColumn";
import {
  AcceptDialog,
  DenyDialog,
  type OfferValues,
} from "@/components/underwriting/UnderwritingAssessmentDialogs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import type { DenyReason } from "@/types/underwriting";

import type { ToolArtifact } from "../../aui/convert";
import { useThreadId } from "../../chat/useThreadId";
import { isDeclineOption } from "../../choices/wire";
import type { WireChoice } from "../../protocol/protocol";
import type { AgentState } from "../../store/agentSlice";
import { selectChoice } from "../../store/selectors";
import type { NativeCard, NativeCardProps } from "..";
import { useOnFake } from "../useOnFake";
import {
  CARD_DECISION_COLUMN,
  CHANGE_HINT,
  CardFooter,
  CardShell,
  EDIT_HINT,
  ReceiptLine,
  StripSection,
  TermsRow,
  WaitingHint,
} from "./CardParts";
import { GenericApproval } from "./ChoiceParts";
import { COPY, Note, Reasons, Terms, choiceActions, idleText, webOptions } from "./decisionParts";
import {
  DECISION_TOOLS,
  dialogNotes,
  dialogOffer,
  dialogPositions,
  isDecisionCard,
  ordinalPosition,
  type DecisionCardPayload,
} from "./payloads";
import { useCardEdits, useOfferEdits } from "./overrides";
import { useApprovalSend } from "./useApprovalSend";
import { useDecide } from "./useDecide";

type AgentRoot = { agent: AgentState };

/**
 * An approve card's edits (its `overrides`): the Offer Builder's terms and the dialog's note, as
 * the press sends them.
 */
function useApproveEdits(choice: WireChoice | null, card: DecisionCardPayload | null) {
  const { editable, edits, offerEdit, offer } = useOfferEdits(choice);
  return {
    offerEdit,
    noteEditable: editable.has("note"),
    /** The dialog's Our Offer: the terms as edited. */
    offer,
    /** What confirming with `note` sends. */
    edits: (note: string) => edits({ shown: { note: card?.note?.text ?? "" }, now: { note } }),
  };
}

/** Why the card's approve is off while a term is outside what an approve accepts. */
export const FIX_TERMS = "Fix the offer terms to approve.";

/**
 * Whether a press went through as an approve (the server records a variant's press as the
 * approve itself): false when it was refused, redrawn or superseded.
 */
const approvedBy = (decided: WireChoice | null) =>
  decided?.status === "decided" && decided.chosen != null && !isDeclineOption(decided.chosen);

/**
 * The card's args_sha256 as of the last render: true once it changed, so a dialog opened on the
 * card it replaced closes unconfirmed (its terms, note and reasons were that card's).
 */
function useRedrawn(choice: WireChoice | null): boolean {
  const sha = choice?.args_sha256 ?? null;
  const [seen, setSeen] = useState(sha);
  if (seen === sha) return false;
  setSeen(sha);
  return true;
}

/**
 * The approve's Confirm Approve dialog and what follows it. The chat approve mirrors the
 * applications UI (FunderAnalysisDashboard approve dialog, approvalOnboarding.ts); change both
 * together. The proposal is confirmed as shown (terms, note), or as the user changed them where
 * the card lets them; Approve + Send Contract, offered by the dashboard's rule, sends the
 * contract the dashboard's way once the approve call has run.
 */
function ApproveConfirm({
  card,
  threadId,
  open,
  onClose,
  approve,
  submitting,
  ran,
  offer,
  noteEditable,
}: {
  /** The approval card: positions and notes. */
  card: DecisionCardPayload;
  /** The card's thread: its current pick may be the picked fake model. */
  threadId: number | null | undefined;
  open: boolean;
  onClose: () => void;
  /**
   * Decides the approval with the dialog's note; false unless it went through (a refusal says
   * why in the column; a redraw shows the user's changes on the card).
   */
  approve: (note: string) => Promise<boolean>;
  submitting: boolean;
  /** The approve call has run. */
  ran: boolean;
  /** The terms as the user changed them on the card; null: as the card shows them. */
  offer: OfferValues | null;
  /** The user may change the proposed note. */
  noteEditable: boolean;
}) {
  const onFake = useOnFake(card, threadId);
  const send = useApprovalSend(card.application.id, open, card.onboarding?.offered, onFake);
  // Only an approve that went through sends, and only once its call ran: on the saved terms.
  const [sendOnRun, setSendOnRun] = useState(false);
  const startSend = send.send;
  useEffect(() => {
    if (!ran || !sendOnRun) return;
    setSendOnRun(false);
    void startSend();
  }, [ran, sendOnRun, startSend]);

  const confirm = async (note: string, sendContract: boolean) => {
    const approved = await approve(note);
    if (approved && sendContract) setSendOnRun(true);
    onClose();
  };

  return (
    <>
      <AcceptDialog
        open={open}
        onClose={onClose}
        activePositions={dialogPositions(card)}
        ordinalPosition={ordinalPosition(card)}
        offerValues={offer ?? dialogOffer(card.terms)}
        onConfirm={(note, sendContract) => void confirm(note, sendContract)}
        submitting={submitting}
        applicationNotes={dialogNotes(card)}
        olderNotes={card.existing_notes_more}
        onboardingNotice={send.onboardingNotice}
        onboardingNoticeError={send.onboardingNoticeError}
        confirmReady={send.confirmReady}
        showSendContractOption={send.showSendContractOption}
        sendContractBlocked={send.sendBlocked}
        phoneSheet
        proposedNote={{
          text: card.note?.text ?? "",
          hint: noteEditable ? undefined : CHANGE_HINT,
          editable: noteEditable,
        }}
      />
      <ContractPreviewModal {...send.previewModal} />
      {send.error && (
        <Alert variant="destructive" className="mx-3.5 mb-3 w-auto py-2">
          <AlertDescription className="text-xs">Approved, but {lower(send.error)}</AlertDescription>
        </Alert>
      )}
      {send.sent && (
        <p role="status" className="mx-3.5 mb-3 text-xs text-muted-foreground">
          {SENT}
        </p>
      )}
    </>
  );
}

/**
 * The card's line once the web sent the contract (the run never hears of it, so the assistant
 * says only "approved"): the backend's `outcome_line` after its "Approved".
 */
const SENT = "Sent the onboarding flow.";

const lower = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

/**
 * A run-less "Change to approve" the server proposed after a decline of this application
 * (`open_follow_up`): its approve_application choice, the open one first, else the newest.
 */
function selectChangeToApprove(
  state: AgentRoot,
  threadId: number | null,
  applicationId: number
): WireChoice | null {
  const thread = threadId == null ? null : state.agent.threads[threadId];
  if (!thread) return null;
  let found: WireChoice | null = null;
  for (const choice of Object.values(thread.choices)) {
    const card = choice.card;
    if (
      choice.tool !== "approve_application" ||
      choice.tool_call_id ||
      !isDecisionCard(card) ||
      card.application.id !== applicationId
    ) {
      continue;
    }
    if (choice.status === "pending") return choice;
    found = choice;
  }
  return found;
}

/**
 * "Change to approve" under an auto-decline: the button, then the suggested offer with the
 * follow-up's Approve / Keep declined, through the same Confirm Approve dialog.
 */
function ChangeToApprove({ applicationId }: { applicationId: number }) {
  const threadId = useThreadId();
  const choice = useSelector((state: AgentRoot) =>
    selectChangeToApprove(state, threadId, applicationId)
  );
  const [open, setOpen] = useState(false);
  // The option the open dialog confirms.
  const [dialog, setDialog] = useState<string | null>(null);
  // As the card's own approve: once the dialog opened, Approve + Send Contract waits for the call.
  const [engaged, setEngaged] = useState(false);
  const { decide, pressed, error, redrawn } = useDecide(choice?.ref ?? null, choice?.args_sha256);
  const card = isDecisionCard(choice?.card) ? choice.card : null;
  const edit = useApproveEdits(choice, card);
  const [problems, setProblems] = useState<string[]>([]);
  const cardRedrawn = useRedrawn(choice);
  if (cardRedrawn && dialog !== null) setDialog(null);
  if (!choice || !card) return null;

  const pending = choice.status === "pending";
  const approved = choice.status === "decided" && !isDeclineOption(choice.chosen);
  // Only while there are terms to fix: a redraw without them leaves no stale problem behind.
  const termsWrong = !!edit.offerEdit && !!card.terms && problems.length > 0;
  if (pending && !open) {
    return (
      <div className="border-t px-3.5 py-2.5">
        <Button
          type="button"
          size="sm"
          className="bg-green-600 hover:bg-green-600/90"
          onClick={() => setOpen(true)}
        >
          <Check className="h-3.5 w-3.5" />
          {webOptions(choice).find((option) => !isDeclineOption(option.id))?.label ||
            "Change to approve"}
        </Button>
      </div>
    );
  }
  if (!pending && !approved) {
    return (
      <div className="border-t px-3.5 py-2.5">
        <ReceiptLine choice={choice} done="Changed to approve" notDone="Kept declined" />
      </div>
    );
  }
  return (
    <div className="border-t" data-testid="change-to-approve">
      <TermsRow
        terms={
          <>
            <Terms card={card} edit={edit.offerEdit} onProblems={setProblems} />
            {pending && (
              <WaitingHint redrawn={redrawn} hint={edit.offerEdit ? EDIT_HINT : CHANGE_HINT} />
            )}
          </>
        }
        column={
          <DecisionColumn
            kind="underwriting"
            layout="auto"
            className={CARD_DECISION_COLUMN}
            outcome={approved && choice.executed !== false ? "approved" : null}
            canAct={pending}
            idle="Saving…"
            actions={choiceActions(
              choice,
              (option) => {
                setEngaged(true);
                setDialog(option);
              },
              (option) => void decide(option),
              pressed,
              termsWrong
            )}
            busy={pressed !== null}
            error={error}
            note={pending && termsWrong ? FIX_TERMS : null}
          />
        }
      />
      {approved && (
        <div className="px-3.5 py-2.5">
          <ReceiptLine choice={choice} done="Changed to approve" notDone="Kept declined" />
        </div>
      )}
      {(pending || dialog || engaged) && (
        <ApproveConfirm
          card={card}
          threadId={choice.thread_id}
          open={dialog !== null}
          onClose={() => setDialog(null)}
          approve={async (note) => {
            if (dialog === null) return false;
            return approvedBy(await decide(dialog, edit.edits(note)));
          }}
          submitting={pressed !== null}
          ran={approved && choice.executed === true}
          offer={edit.offer}
          noteEditable={edit.noteEditable}
        />
      )}
    </div>
  );
}

function Decision({
  card,
  choiceCard,
  choice,
  artifact,
}: {
  card: DecisionCardPayload;
  /** The approval card (positions, notes), while there is a choice. */
  choiceCard: DecisionCardPayload | null;
  choice: WireChoice | null;
  artifact: ToolArtifact;
}) {
  const copy = COPY[card.kind];
  const { decide, pressed, error, redrawn } = useDecide(choice?.ref ?? null, choice?.args_sha256);
  const approveEdit = useApproveEdits(
    card.kind === "approve_application" ? choice : null,
    choiceCard
  );
  const declineEdit = useCardEdits(card.kind === "decline_application" ? choice : null);
  // What is wrong with the terms as edited: the approve waits until they are fixed.
  const [problems, setProblems] = useState<string[]>([]);
  // The open dialog, and the option it confirms.
  const [dialog, setDialog] = useState<{ kind: "accept" | "deny"; option: string } | null>(null);
  // Once a dialog opened, its parts stay mounted: Approve + Send Contract waits for the call.
  const [engaged, setEngaged] = useState(false);
  const cardRedrawn = useRedrawn(choice);
  if (cardRedrawn && dialog !== null) setDialog(null);
  const pending = choice?.status === "pending";
  const ran = artifact.state === "success";
  const failed = artifact.state === "failed";
  // Only while there are terms to fix: a redraw without them leaves no stale problem behind.
  const termsWrong = !!approveEdit.offerEdit && !!card.terms && problems.length > 0;

  const openDialog = (kind: "accept" | "deny", option: string) => {
    setEngaged(true);
    setDialog({ kind, option });
  };
  const onApprove = (option: string) => {
    if (card.kind === "approve_application") openDialog("accept", option);
    else if (card.kind === "decline_application") openDialog("deny", option);
    else void decide(option); // Fund is one tap, as in the app.
  };

  const column = (
    <DecisionColumn
      kind={copy.kind}
      layout="auto"
      className={CARD_DECISION_COLUMN}
      outcome={ran ? copy.outcome : null}
      canAct={pending}
      idle={idleText(copy, choice, failed)}
      actions={
        choice && pending
          ? choiceActions(choice, onApprove, (option) => void decide(option), pressed, termsWrong)
          : undefined
      }
      busy={pressed !== null}
      error={error}
      note={pending && termsWrong ? FIX_TERMS : null}
    />
  );

  return (
    <CardShell
      icon={copy.icon}
      title={copy.title}
      // The status it moves to only once the call ran; until then (or when nothing changed), the
      // one it is in.
      status={ran ? card.status.to : card.status.from}
      data-decision={card.kind}
      data-state={pending ? "pending" : ran ? "ran" : (choice?.status ?? artifact.state)}
    >
      <StripSection strip={card.strip} />
      <TermsRow
        terms={
          <>
            {card.kind === "decline_application" ? (
              <Reasons card={card} />
            ) : (
              <Terms card={card} edit={approveEdit.offerEdit} onProblems={setProblems} />
            )}
            {card.kind === "approve_application" && <Note card={card} />}
            {pending && (
              <WaitingHint
                redrawn={redrawn}
                hint={
                  approveEdit.offerEdit || declineEdit.editable.size ? copy.editHint : copy.hint
                }
              />
            )}
          </>
        }
        column={column}
      />
      {card.kind === "approve_application" && choiceCard && (pending || engaged) && (
        <ApproveConfirm
          card={choiceCard}
          threadId={choice?.thread_id}
          open={dialog?.kind === "accept"}
          onClose={() => setDialog(null)}
          approve={async (note) => {
            if (dialog?.kind !== "accept") return false;
            return approvedBy(await decide(dialog.option, approveEdit.edits(note)));
          }}
          submitting={pressed !== null}
          ran={ran}
          offer={approveEdit.offer}
          noteEditable={approveEdit.noteEditable}
        />
      )}
      {card.kind === "decline_application" && pending && (
        <DenyDialog
          open={dialog?.kind === "deny"}
          onClose={() => setDialog(null)}
          onConfirm={(reasons, notes) => {
            if (dialog?.kind !== "deny") return;
            const shown = { reasons: card.reasons, note: card.note?.text ?? "" };
            void decide(dialog.option, declineEdit.edits({ shown, now: { reasons, note: notes } }));
            setDialog(null);
          }}
          submitting={pressed !== null}
          applicationNotes={choiceCard ? dialogNotes(choiceCard) : undefined}
          phoneSheet
          proposed={{
            reasons: card.reasons as DenyReason[],
            notes: card.note?.text ?? "",
            hint: declineEdit.editable.size ? undefined : copy.hint,
            editable: declineEdit.editable.size > 0,
          }}
        />
      )}
      {card.kind === "decline_application" && ran && (
        <ChangeToApprove applicationId={card.application.id} />
      )}
      <CardFooter applicationId={card.application.id}>
        {failed ? (
          <p className="min-w-0 text-xs font-medium text-destructive">{artifact.line}</p>
        ) : ran && !choice && card.kind === "decline_application" ? (
          // A decline with no approval to receipt is the agent's own: not a good outcome.
          <ReceiptLine choice={null} done="Declined automatically" notDone="" tone="declined" />
        ) : ran || (choice && !pending) ? (
          <ReceiptLine choice={choice} done={copy.done} notDone={copy.notDone} />
        ) : null}
      </CardFooter>
    </CardShell>
  );
}

/** approve_application, decline_application, fund_application: the decision as the app shows it. */
function DecisionCard({ artifact }: NativeCardProps) {
  const choice = useSelector((state: AgentRoot) =>
    artifact.choiceRef ? selectChoice(state, artifact.choiceRef) : null
  );
  const choiceCard = isDecisionCard(choice?.card) ? choice.card : null;
  const card = isDecisionCard(artifact.resultCard) ? artifact.resultCard : choiceCard;
  if (!card) return <GenericApproval artifact={artifact} />;
  return <Decision card={card} choiceCard={choiceCard} choice={choice} artifact={artifact} />;
}

export const DECISION_CARD: NativeCard = {
  Card: DecisionCard,
  // A call with neither card (an older message, a call that failed before drawing one) keeps its
  // generic approval card or line.
  shows: (artifact) =>
    isDecisionCard(artifact.resultCard) ||
    (DECISION_TOOLS as readonly (string | null)[]).includes(artifact.choiceCardKind),
};
